import { useCallback } from 'react'

import { Locations, track, trackKeyGrowthEvent } from '@mc/track-event'
import {
  ActionTypes,
  FUNNEL_STATE_ID,
  KeyTypes,
  saveUserData,
  saveMultipleUserData,
  getTrackedIds,
} from '@mc/user-data'

import { QuizMachineContext } from './context'
import type { InterstitialOpts, Screen, Opts, EventOptions } from './types'
import { STEP_NAME } from './machine'

const PERSONALIZATION_INSTL_CLICKED = 'Personalization Interstitial Clicked'
const PERSONALIZATION_INSTL_VIEWED = 'Personalization Interstitial Viewed'
export const PERSONALIZATION_STEP_COMPLETED = 'Personalization Step Completed'
export const PERSONALIZATION_STEP_CLICK = 'Personalization Click'

export const useQuizEvents = () => {
  const { step, stepName } = QuizMachineContext.useSelector(({ context }) => ({
    step: context.step,
    stepName: context.stepName,
  }))
  const interstitialScreen = QuizMachineContext.useSelector(
    ({ context }) => context.interstitialScreen,
  )
  // either a positive number, or undefined
  const numberOfSubcategories = QuizMachineContext.useSelector(
    ({ context }) => (context.subcatInterests || []).length || undefined,
  )
  const [state] = QuizMachineContext.useActor()

  const trackOptionSelected = useCallback(
    ({
      value,
      checked,
      subStepNumber,
    }: Pick<EventOptions, 'value' | 'checked'> &
      Pick<Opts, 'subStepNumber'>) => {
      trackPersonalization({
        step,
        step_name: stepName,
        category: 'personalization-input',
        location: Locations.BODY,
        page_source: 'Find My Classes Quiz',
        page_path: window.location.pathname,
        action: value,
        subStepNumber,
        checked,
      })
    },
    [step, stepName],
  )

  const trackClickInterstitial = useCallback(
    ({ action }: Pick<Opts, 'action'>) => {
      trackInterstitial(PERSONALIZATION_INSTL_CLICKED, {
        step,
        step_name: stepName,
        interstitial_screen: interstitialScreen as Screen,
        page_source: 'Find My Classes Quiz',
        page_path: window.location.pathname,
        action,
        numberOfSubcategories,
      })
      if (stepName === STEP_NAME.TIME_QUESTION) {
        saveUserData({
          key: KeyTypes.FUNNEL_STATE,
          id: FUNNEL_STATE_ID,
          actionType: ActionTypes.USER_COMPLETED_QUIZ,
          eventName: PERSONALIZATION_INSTL_CLICKED,
        })
      }
    },
    [interstitialScreen, numberOfSubcategories, step, stepName],
  )

  const trackSkipButtonClick = useCallback(() => {
    const params = {
      step,
      step_name: stepName,
      category: 'category-selection',
      location: Locations.BODY,
      page_source: 'Find My Classes Quiz',
      page_path: window.location.pathname,
      action: 'Skip',
    } as const
    if (state.tags.has('interstitial')) {
      trackInterstitialClickedKeyGrowthEvent(params)
    } else {
      trackStepComplete(params)
    }
  }, [state.tags, step, stepName])

  const trackBackButtonClicked = useCallback(() => {
    const trackProps: Opts = {
      step,
      step_name: stepName,
      category: 'personalization-input',
      location: Locations.BODY,
      page_source: 'Find My Classes Quiz',
      page_path: window.location.pathname,
      action: 'Back',
    }

    if (interstitialScreen) {
      trackClickInterstitial(trackProps)
    } else {
      trackPersonalization(trackProps)
    }
  }, [interstitialScreen, step, stepName, trackClickInterstitial])

  const trackSkipQuestionButtonClicked = useCallback(() => {
    const trackProps: Opts = {
      step,
      step_name: stepName,
      category: 'personalization-input',
      location: Locations.BODY,
      page_source: 'Find My Classes Quiz',
      page_path: window.location.pathname,
      action: 'Skip',
    }

    if (interstitialScreen) {
      trackClickInterstitial(trackProps)
    } else {
      trackPersonalization(trackProps)
    }
  }, [interstitialScreen, step, stepName, trackClickInterstitial])

  type RequiredOpts<Keys extends keyof Opts> = {
    [K in Keys]-?: NonNullable<Opts[K]>
  }

  const trackSubcatPersonalizationComplete = useCallback(
    ({
      selectedCategories,
      selectedSubcategories,
      selectedCategoryIds,
      selectedSubcategoryIds,
      subStepNumber,
    }: RequiredOpts<
      | 'selectedCategories'
      | 'selectedSubcategories'
      | 'selectedCategoryIds'
      | 'selectedSubcategoryIds'
      | 'subStepNumber'
    >) => {
      trackStepComplete({
        step,
        step_name: stepName,
        category: 'personalization-input',
        location: Locations.BODY,
        page_source: 'Find My Classes Quiz',
        page_path: window.location.pathname,
        subStepNumber,
        selectedCategories,
        selectedSubcategories,
        selectedCategoryIds,
        selectedSubcategoryIds,
        action: 'Next',
      })
    },
    [step, stepName],
  )

  const trackPrimerAnswerSubmitted = useCallback(
    ({
      selectedOptions,
      selectedCategories,
      selectedCategoryIds,
    }: Pick<
      Opts,
      'selectedCategories' | 'selectedOptions' | 'selectedCategoryIds'
    >) => {
      trackStepComplete({
        step,
        step_name: stepName,
        category: 'personalization-input',
        location: Locations.BODY,
        page_source: 'Find My Classes Quiz',
        page_path: window.location.pathname,
        action: 'Next',
        selectedOptions,
        selectedCategories,
        selectedCategoryIds,
      })
    },
    [step, stepName],
  )

  const trackMotivationRefinementComplete = useCallback(
    ({
      selectedCategories,
      selectedCategoryIds,
    }: Pick<Opts, 'selectedCategories' | 'selectedCategoryIds'>) => {
      trackStepComplete({
        step,
        step_name: stepName,
        category: 'personalization-input',
        location: Locations.BODY,
        page_source: 'Find My Classes Quiz',
        page_path: window.location.pathname,
        action: 'Next',
        selectedCategories,
        selectedCategoryIds,
      })
    },
    [step, stepName],
  )

  const trackAnswerSubmitted = useCallback(
    ({ selectedOptions }: Pick<Opts, 'selectedOptions'>) => {
      trackStepComplete({
        step,
        step_name: stepName,
        category: 'personalization-input',
        location: Locations.BODY,
        page_source: 'Find My Classes Quiz',
        page_path: window.location.pathname,
        action: 'Next',
        selectedOptions,
      })
    },
    [step, stepName],
  )

  const trackInterstitialScreenViewed = useCallback(() => {
    trackInterstitial(PERSONALIZATION_INSTL_VIEWED, {
      step,
      step_name: stepName,
      numberOfSubcategories,
      interstitial_screen: interstitialScreen as Screen,
      page_source: 'Find My Classes Quiz',
      page_path: window.location.pathname,
    })
  }, [interstitialScreen, numberOfSubcategories, step, stepName])

  return {
    trackSkipButtonClick,
    trackBackButtonClicked,
    trackOptionSelected,
    trackInterstitialScreenViewed,
    trackClickInterstitial,
    trackPrimerAnswerSubmitted,
    trackAnswerSubmitted,
    trackMotivationRefinementComplete,
    trackSubcatPersonalizationComplete,
    trackSkipQuestionButtonClicked,
  }
}

export const trackStepComplete = (opts: Opts) => {
  const additionalProps = userDataTrackProps(opts)

  trackKeyGrowthEvent(PERSONALIZATION_STEP_COMPLETED, {
    ...opts,
    ...additionalProps,
  })

  saveMultipleUserDataProps(opts)
}

const saveMultipleUserDataProps = (opts: Opts) => {
  const { selectedCategoryIds, selectedSubcategoryIds } = opts

  if (typeof selectedCategoryIds !== 'undefined') {
    saveMultipleUserData({
      key: KeyTypes.CATEGORY,
      ids: selectedCategoryIds,
      actionType: ActionTypes.DECLARED,
      eventName: PERSONALIZATION_STEP_COMPLETED,
    })
  }

  if (typeof selectedSubcategoryIds !== 'undefined') {
    saveMultipleUserData({
      key: KeyTypes.SUBCATEGORY,
      ids: selectedSubcategoryIds,
      actionType: ActionTypes.DECLARED,
      eventName: PERSONALIZATION_STEP_COMPLETED,
    })
  }
}

const userDataTrackProps = (opts: Opts) => {
  const { selectedCategoryIds, selectedSubcategoryIds } = opts

  let additionalProps = {}

  if (typeof selectedCategoryIds !== 'undefined') {
    additionalProps = {
      ...additionalProps,
      stored_category_ids: getTrackedIds({
        key: KeyTypes.CATEGORY,
        ids: selectedCategoryIds,
      }),
    }
  }

  if (typeof selectedSubcategoryIds !== 'undefined') {
    additionalProps = {
      ...additionalProps,
      stored_subcategory_ids: getTrackedIds({
        key: KeyTypes.SUBCATEGORY,
        ids: selectedSubcategoryIds,
      }),
    }
  }

  return additionalProps
}

const trackInterstitialClickedKeyGrowthEvent = (opts: Opts) => {
  trackKeyGrowthEvent(PERSONALIZATION_INSTL_CLICKED, opts)
}

const trackPersonalization = (opts: Opts) => {
  track(PERSONALIZATION_STEP_CLICK, opts)
}

const trackInterstitial = (
  evtName:
    | typeof PERSONALIZATION_INSTL_VIEWED
    | typeof PERSONALIZATION_INSTL_CLICKED,
  opts: InterstitialOpts,
) => {
  track(evtName, opts)
}
